interface LogEntry {
  timestamp: string;
  type: 'CHAT' | 'ERROR' | 'SYSTEM' | 'PARSING';
  message: string;
  metadata?: Record<string, any>;
}

/**
 * Security & Privacy: ChatLogger stores chat transcripts which may contain PII.
 * - Logging is DISABLED by default in production (requires explicit flag)
 * - Uses sessionStorage (not localStorage) to auto-clear on tab close
 * - Implements TTL to limit data retention
 */
class ChatLogger {
  private currentSessionId: string | null = null;
  private readonly STORAGE_KEY_PREFIX = 'chat_log_';
  private readonly MAX_LOG_AGE_MS = 3600000; // 1 hour TTL

  constructor() {
    // No initialization needed for browser storage
  }

  /**
   * Check if logging is enabled via feature flag.
   * Default: false (disabled) for privacy and security.
   */
  private isLoggingEnabled(): boolean {
    try {
      // Check global widget config for logging flag
      const flags = (window as any).__WW__?.flags;
      return flags?.loggingEnabled === true;
    } catch {
      return false;
    }
  }

  /**
   * Get the appropriate storage backend.
   * Uses sessionStorage (clears on tab close) instead of localStorage (persists indefinitely).
   */
  private getStorage(): Storage {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : localStorage;
  }

  private getStorageKey(sessionId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${sessionId}`;
  }

  private formatLogEntry(entry: LogEntry): string {
    const timestamp = new Date().toISOString();
    const metadataStr = entry.metadata ? `\nMetadata: ${JSON.stringify(entry.metadata, null, 2)}` : '';
    return `[${timestamp}] [${entry.type}] ${entry.message}${metadataStr}\n`;
  }

  setSession(sessionId: string) {
    this.currentSessionId = sessionId;
    this.log({
      type: 'SYSTEM',
      message: `New chat session started`,
      metadata: { sessionId }
    });
  }

  log(entry: Omit<LogEntry, 'timestamp'>) {
    // Security: Only log if explicitly enabled via feature flag
    if (!this.isLoggingEnabled()) {
      return;
    }

    if (!this.currentSessionId) {
      console.error('No active session for logging');
      return;
    }

    const logEntry: LogEntry = {
      ...entry,
      timestamp: new Date().toISOString()
    };

    const logContent = this.formatLogEntry(logEntry);
    const storageKey = this.getStorageKey(this.currentSessionId);

    try {
      const storage = this.getStorage();
      // Get existing logs and trim old entries
      const existingLogs = storage.getItem(storageKey) || '';
      const trimmedLogs = this.trimOldLogs(existingLogs);
      // Append new log
      storage.setItem(storageKey, trimmedLogs + logContent);
    } catch (error) {
      console.error('Failed to write to log storage:', error);
    }
  }

  /**
   * Remove log entries older than MAX_LOG_AGE_MS to limit data retention.
   */
  private trimOldLogs(logs: string): string {
    if (!logs) return '';
    
    const lines = logs.split('\n');
    const now = Date.now();
    const cutoff = now - this.MAX_LOG_AGE_MS;
    const kept: string[] = [];

    for (const line of lines) {
      // Parse timestamp from log line format: [2024-01-01T12:00:00.000Z]
      const match = /^\[(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\]/.exec(line);
      if (match) {
        try {
          const timestamp = new Date(match[1]).getTime();
          if (timestamp >= cutoff) {
            kept.push(line);
          }
        } catch {
          // Keep lines with unparseable timestamps
          kept.push(line);
        }
      } else if (line.trim()) {
        // Keep non-empty lines without timestamps (metadata, etc.)
        kept.push(line);
      }
    }

    return kept.join('\n');
  }

  logChat(message: string, isUser: boolean, metadata?: Record<string, any>) {
    this.log({
      type: 'CHAT',
      message: `${isUser ? 'User' : 'Bot'}: ${message}`,
      metadata
    });
  }

  logError(error: Error | string, metadata?: Record<string, any>) {
    this.log({
      type: 'ERROR',
      message: error instanceof Error ? error.message : error,
      metadata: {
        ...metadata,
        stack: error instanceof Error ? error.stack : undefined
      }
    });
  }

  logParsingIssue(issue: string, metadata?: Record<string, any>) {
    this.log({
      type: 'PARSING',
      message: issue,
      metadata
    });
  }

  getSessionLogs(sessionId: string): string {
    if (!this.isLoggingEnabled()) {
      return '';
    }
    try {
      const storage = this.getStorage();
      const storageKey = this.getStorageKey(sessionId);
      return storage.getItem(storageKey) || '';
    } catch (error) {
      console.error('Failed to read log storage:', error);
      return '';
    }
  }

  getAllSessionIds(): string[] {
    if (!this.isLoggingEnabled()) {
      return [];
    }
    const storage = this.getStorage();
    const sessions: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
        sessions.push(key.replace(this.STORAGE_KEY_PREFIX, ''));
      }
    }
    return sessions;
  }

  deleteSessionLogs(sessionId: string): boolean {
    try {
      const storage = this.getStorage();
      const storageKey = this.getStorageKey(sessionId);
      storage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('Failed to delete log storage:', error);
      return false;
    }
  }

  clearAllLogs(): boolean {
    try {
      const sessions = this.getAllSessionIds();
      sessions.forEach(sessionId => {
        this.deleteSessionLogs(sessionId);
      });
      return true;
    } catch (error) {
      console.error('Failed to clear logs:', error);
      return false;
    }
  }
}

// Export a singleton instance
export const chatLogger = new ChatLogger(); 